import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { Save } from '@mui/icons-material';

const NOTE_TEMPLATES = [
  {
    group: 'Materials',
    notes: [
      'PET bottles - incoming raw materials',
      'Caps and closures - incoming packaging materials',
      'Labels - incoming packaging materials',
      'Water treatment chemicals - incoming materials',
      'Cardboard cases - incoming packaging materials',
      'Shrink wrap - incoming packaging materials',
      'Pallets - incoming logistics materials'
    ]
  },
  {
    group: 'Product',
    notes: ['Product1 - incoming finished goods', 'Product2 - incoming finished goods']
  }
];

interface TrailerNotesEditorProps {
  initialNotes: string;
  saving: boolean;
  onSave: (notes: string) => void;
  onCancel: () => void;
}

// Keeps the draft text in local state so typing only re-renders this
// component, not the whole Trailers page and its table.
const TrailerNotesEditor = ({
  initialNotes,
  saving,
  onSave,
  onCancel
}: TrailerNotesEditorProps) => {
  const [notes, setNotes] = useState(initialNotes);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Set note</InputLabel>
          <Select
            value=""
            onChange={(e) => setNotes(e.target.value)}
            label="Set note"
            disabled={saving}
          >
            <MenuItem value="" disabled>
              <Typography variant="body2" color="text.secondary">
                Select a template...
              </Typography>
            </MenuItem>
            {NOTE_TEMPLATES.flatMap(({ group, notes: groupNotes }, index) => [
              <MenuItem
                key={`group-${group}`}
                disabled
                sx={{ fontWeight: 'bold', color: 'primary.main', mt: index > 0 ? 1 : 0 }}
              >
                {group}
              </MenuItem>,
              ...groupNotes.map((note) => (
                <MenuItem key={note} value={note}>
                  <Typography variant="body2" sx={{ pl: 2 }}>
                    {note}
                  </Typography>
                </MenuItem>
              ))
            ])}
          </Select>
        </FormControl>
      </Box>

      <TextField
        fullWidth
        multiline
        rows={4}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Enter notes about this trailer..."
        variant="outlined"
        disabled={saving}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        <Button size="small" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => onSave(notes)}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : <Save />}
        >
          {saving ? 'Saving...' : 'Save Notes'}
        </Button>
      </Box>
    </Box>
  );
};

export default TrailerNotesEditor;
